import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  CloudServerOutlined,
  SecurityScanOutlined,
  DatabaseOutlined,
  CustomerServiceOutlined,
  LaptopOutlined,
  FileTextOutlined,
  FileAddOutlined,
  BookOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TeamOutlined,
  RadarChartOutlined,
  ReconciliationOutlined,
  ScheduleOutlined,
} from '@ant-design/icons';
import { MdOutlineSmartToy } from 'react-icons/md';
import { useAuth } from '../../context/AuthContext';
import { TIERS, ROLES } from '../../constants/roles';

// Optimized logo images (the wordmark follows the theme mode — see theme/csgLogo.js)
import { useCsgLogo } from '../../theme/csgLogo';
import csgIcon from '../../img/optimized/csg-icon.webp';
import csgIconPng from '../../img/optimized/csg-icon.png';

const SidebarContainer = styled.aside`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: ${({ theme, $collapsed }) =>
    $collapsed ? theme.layout.sidebarCollapsedWidth : theme.layout.sidebarWidth};
  background: ${({ theme }) => theme.colors.sidebarBg};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  z-index: ${({ theme }) => theme.zIndex.sidebar};
  transition: all 0.3s ease;
  overflow-y: auto;

  ${({ theme }) => theme.mediaQuery.maxMd} {
    transform: translateX(${({ $mobileOpen }) => $mobileOpen ? '0' : '-100%'});
    width: ${({ theme }) => theme.layout.sidebarWidth};
  }
`;

const SidebarHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => $collapsed ? 'center' : 'space-between'};
  height: ${({ theme }) => theme.layout.headerHeight};
  box-sizing: border-box;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  overflow: hidden;
  transition: all 0.3s ease;
`;

const LogoImage = styled.picture`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  img {
    height: ${({ $size }) => $size === 'small' ? '40px' : '44px'};
    max-width: ${({ $size }) => $size === 'small' ? '40px' : '170px'};
    width: auto;
    object-fit: contain;
    transition: all 0.3s ease;
  }
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: ${({ theme }) => theme.transitions.normal};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;

  /* Neutral hover, like every other control in the shell: the accent is never spent on a hover */
  &:hover {
    background: ${({ theme }) => theme.colors.sidebarHover};
    color: ${({ theme }) => theme.colors.text};
  }

  ${({ theme }) => theme.mediaQuery.maxMd} {
    display: none;
  }
`;

const Navigation = styled.nav`
  padding: 12px 10px;
`;

const MenuSection = styled.div`
  margin-bottom: 6px;
`;

/* Inter kicker; secondary, not tertiary (tertiary is 4.26:1 on the rail) */
const SectionLabel = styled.div`
  padding: 16px 12px 6px;
  font-size: 10.5px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.12em;
  opacity: ${({ $collapsed }) => $collapsed ? '0' : '1'};
  height: ${({ $collapsed }) => $collapsed ? '0' : 'auto'};
  overflow: hidden;
  transition: opacity 0.3s ease;
`;

// A three-level tree: item (secondary, 500) < hover (primary) < active (ink, 600,
// on the accent-tint pill with an accent-text icon). The pill is the ONE marker.
const MenuItem = styled(Link)`
  display: flex;
  align-items: center;
  margin: 1px 0;
  padding: 9px 12px;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.sidebarText};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-size: 13.5px;
  transition: background 0.15s ease, color 0.15s ease;
  position: relative;

  &:hover {
    background: ${({ theme }) => theme.colors.sidebarHover};
    color: ${({ theme }) => theme.colors.text};

    .icon {
      color: ${({ theme }) => theme.colors.text};
    }
  }

  &.active {
    background: ${({ theme }) => theme.colors.sidebarActive};
    color: ${({ theme }) => theme.colors.sidebarTextActive};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};

    .icon {
      color: ${({ theme }) => theme.colors.textAccent};
    }
  }

  .icon {
    margin-right: ${({ $collapsed }) => $collapsed ? '0' : '12px'};
    font-size: 17px;
    min-width: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.colors.textSecondary};
    transition: color 0.15s ease;
  }

  .label {
    opacity: ${({ $collapsed }) => $collapsed ? '0' : '1'};
    width: ${({ $collapsed }) => $collapsed ? '0' : 'auto'};
    overflow: hidden;
    white-space: nowrap;
    transition: opacity 0.3s ease;

    ${({ theme }) => theme.mediaQuery.maxMd} {
      opacity: 1;
      width: auto;
    }
  }
`;

// Navigation items for ODIN1 Dashboard with role-based access
// allowedRoles: empty array = all authenticated users can access
const navigationItems = [
  {
    path: '/',
    icon: DashboardOutlined,
    label: 'Dashboard',
    section: 'main',
    allowedRoles: [], // All authenticated users
  },
  {
    path: '/m365',
    icon: CloudServerOutlined,
    label: 'Microsoft 365',
    section: 'platforms',
    allowedRoles: TIERS.PLATFORMS,
  },
  {
    path: '/security',
    icon: SecurityScanOutlined,
    label: 'Sophos',
    section: 'platforms',
    allowedRoles: TIERS.PLATFORMS,
  },
  {
    path: '/acronis',
    icon: DatabaseOutlined,
    label: 'Acronis',
    section: 'platforms',
    allowedRoles: TIERS.PLATFORMS,
  },
  {
    path: '/service-desk',
    icon: CustomerServiceOutlined,
    label: 'Autotask',
    section: 'platforms',
    allowedRoles: TIERS.PLATFORMS,
  },
  {
    path: '/scalepad',
    icon: LaptopOutlined,
    label: 'ScalePad',
    section: 'platforms',
    allowedRoles: TIERS.PLATFORMS,
  },
  {
    path: '/datto',
    icon: DatabaseOutlined,
    label: 'Datto',
    section: 'platforms',
    allowedRoles: TIERS.PLATFORMS,
  },
  {
    path: '/agents',
    icon: MdOutlineSmartToy,
    label: 'Agents',
    section: 'agent',
    allowedRoles: TIERS.AGENTS_HUB,
  },
  {
    path: '/reports',
    icon: FileTextOutlined,
    label: 'Reports',
    section: 'tools',
    allowedRoles: TIERS.REPORTS,
  },
  {
    path: '/engineer-performance',
    icon: TeamOutlined,
    label: 'Engineer Performance',
    section: 'tools',
    allowedRoles: TIERS.ENGINEER_PERFORMANCE,
  },
  {
    path: '/reports/recommendations',
    icon: FileAddOutlined,
    label: 'Recommendations',
    section: 'tools',
    allowedRoles: TIERS.REPORTS,
  },
  {
    path: '/knowledge',
    icon: BookOutlined,
    label: 'Knowledge Base',
    section: 'tools',
    allowedRoles: TIERS.ADMIN_ONLY,
  },
  {
    path: '/settings',
    icon: SettingOutlined,
    label: 'Settings',
    section: 'tools',
    allowedRoles: TIERS.ADMIN_ONLY,
  },
  {
    path: '/admin/customer-mappings',
    icon: TeamOutlined,
    label: 'Customer Mappings',
    section: 'admin',
    allowedRoles: TIERS.ADMIN_ONLY,
  },
  {
    path: '/onboarding',
    icon: FileAddOutlined,
    label: 'Customer Onboarding',
    section: 'onboarding',
    allowedRoles: TIERS.ONBOARDING,
  },
  {
    path: '/walk-round',
    icon: RadarChartOutlined,
    label: 'OD1N Walk-Round',
    section: 'onboarding',
    allowedRoles: TIERS.WALKROUND,
  },
  {
    path: '/accounts/reconcile',
    icon: ReconciliationOutlined,
    label: 'Invoice Reconcile',
    section: 'accounts',
    allowedRoles: TIERS.RECONCILE,
  },
  {
    path: '/management',
    icon: ScheduleOutlined,
    label: 'Management',
    section: 'management',
    allowedRoles: TIERS.MANAGEMENT,
  },
];

const DashboardSidebar = ({ collapsed, mobileOpen, onToggle, onMobileClose }) => {
  const location = useLocation();
  const csgLogo = useCsgLogo();
  const { hasAnyRole } = useAuth();

  const handleMenuItemClick = () => {
    if (window.innerWidth <= 768) {
      onMobileClose();
    }
  };

  // Check if user can access a navigation item based on their roles
  const canAccessItem = (item) => {
    // If no roles specified, allow all authenticated users
    if (!item.allowedRoles || item.allowedRoles.length === 0) {
      return true;
    }
    return hasAnyRole(item.allowedRoles);
  };

  const renderMenuItems = (section) => {
    return navigationItems
      .filter(item => item.section === section && canAccessItem(item))
      .map(item => {
        const IconComponent = item.icon;
        const isActive = location.pathname === item.path;

        return (
          <MenuItem
            key={item.path}
            to={item.path}
            className={isActive ? 'active' : ''}
            $collapsed={collapsed}
            onClick={handleMenuItemClick}
          >
            <div className="icon">
              <IconComponent />
            </div>
            <span className="label">{item.label}</span>
          </MenuItem>
        );
      });
  };

  return (
    <SidebarContainer
      data-testid="dashboard-sidebar"
      $collapsed={collapsed}
      $mobileOpen={mobileOpen}
    >
      <SidebarHeader $collapsed={collapsed}>
        <Logo>
          {collapsed ? (
            <LogoImage $size="small">
              <source srcSet={csgIcon} type="image/webp" />
              <img src={csgIconPng} alt="CSG" />
            </LogoImage>
          ) : (
            <LogoImage>
              <source srcSet={csgLogo.webp} type="image/webp" />
              <img src={csgLogo.png} alt="CSG" />
            </LogoImage>
          )}
        </Logo>
        <CollapseButton onClick={onToggle}>
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </CollapseButton>
      </SidebarHeader>

      <Navigation>
        <MenuSection>
          {renderMenuItems('main')}
        </MenuSection>

        <SectionLabel $collapsed={collapsed}>Platforms</SectionLabel>
        <MenuSection>
          {renderMenuItems('platforms')}
        </MenuSection>

        {renderMenuItems('agent').length > 0 && (
          <>
            <SectionLabel $collapsed={collapsed}>AI Assistant</SectionLabel>
            <MenuSection>
              {renderMenuItems('agent')}
            </MenuSection>
          </>
        )}

        <SectionLabel $collapsed={collapsed}>Tools</SectionLabel>
        <MenuSection>
          {renderMenuItems('tools')}
        </MenuSection>

        {renderMenuItems('onboarding').length > 0 && (
          <>
            <SectionLabel $collapsed={collapsed}>Onboarding</SectionLabel>
            <MenuSection>
              {renderMenuItems('onboarding')}
            </MenuSection>
          </>
        )}

        {renderMenuItems('accounts').length > 0 && (
          <>
            <SectionLabel $collapsed={collapsed}>Accounts</SectionLabel>
            <MenuSection>
              {renderMenuItems('accounts')}
            </MenuSection>
          </>
        )}

        {renderMenuItems('management').length > 0 && (
          <>
            <SectionLabel $collapsed={collapsed}>Management</SectionLabel>
            <MenuSection>
              {renderMenuItems('management')}
            </MenuSection>
          </>
        )}

        {hasAnyRole([ROLES.ADMIN]) && renderMenuItems('admin').length > 0 && (
          <>
            <SectionLabel $collapsed={collapsed}>Admin</SectionLabel>
            <MenuSection>
              {renderMenuItems('admin')}
            </MenuSection>
          </>
        )}
      </Navigation>
    </SidebarContainer>
  );
};

DashboardSidebar.propTypes = {
  collapsed: PropTypes.bool,
  mobileOpen: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onMobileClose: PropTypes.func.isRequired,
};

DashboardSidebar.defaultProps = {
  collapsed: false,
  mobileOpen: false,
};

export default DashboardSidebar;
